// Product Stats
export interface ProductStatsData {
  id: number;
  codigo: string;
  nombre?: string;
  sku?: string;
  color?: string;
  categoria?: string;
  grupo?: string;
  stock: number;
  total_quantity?: number;
}
// Tipos de usuario y autenticación
export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  telefono?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  full_name: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

// Tipos para gestión de usuarios (admin)
export interface UserCreateRequest {
  email: string;
  name: string;
  password: string;
  role: 'admin' | 'user';
  telefono?: string;
}

export interface UserUpdateRequest {
  email?: string;
  name?: string;
  role?: 'admin' | 'user';
  is_active?: boolean;
  telefono?: string;
}

export interface UserFilters {
  email?: string;
  role?: string;
  is_active?: boolean;
  skip?: number;
  limit?: number;
}

// Tipos de productos
export interface Product {
  id: number;
  sku?: string;
  codigo: string;
  color?: string;
  grupo?: string;
  categoria?: string;
  descripcion?: string;
  stock?: number;
  largo?: number;
  ancho?: number;
  espesor?: number;
  ancho_util?: number;
  precio_unitario: number;
  precio_m2?: number;
  currency?: string;  // Moneda (USD, EUR, etc.)
  descuento?: number;
  estructura_m2?: string;  // Lista de productos: "codigo,cantidad\ncodigo,cantidad"
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface ProductFormData {
  sku: string;
  codigo: string;
  color?: string;
  grupo?: string;
  categoria?: string;
  descripcion?: string;
  stock?: number;
  largo?: number;
  ancho?: number;
  espesor?: number;
  ancho_util?: number;
  precio_unitario: number;
  precio_m2?: number;
  currency?: string;  // Moneda (USD, EUR, etc.)
  descuento?: number;
  estructura_m2?: string;  // Lista de productos: "codigo,cantidad\ncodigo,cantidad"
  is_active?: boolean;
}

export interface ProductFilters {
  search?: string;
  categoria?: string;
  grupo?: string;
  color?: string;
  is_active?: boolean;
  active_only?: boolean;
  limit?: number;  // Límite de productos a mostrar
}

// Tipos de presupuestos - NUEVO MODELO

// Environment Materials
export interface EnvironmentMaterial {
  id: number;
  environment_id: number;
  product_id?: number;
  descripcion: string;
  codigo: string;
  sku?: string;
  foto?: string;
  unidades: number;
  metros2?: number;
  precio_unitario: number;
  observaciones?: string;
  monto: number;
  es_in_situ: boolean;
  currency: string;
  con_desperdicio?: boolean;
  con_estructura?: boolean;
  created_at: string;
  updated_at?: string;
}

export interface EnvironmentMaterialCreate {
  product_id?: number;
  descripcion: string;
  codigo: string;
  sku?: string;
  foto?: string;
  unidades: number;
  metros2?: number;
  precio_unitario: number;
  observaciones?: string;
  monto: number;
  es_in_situ?: boolean;
  currency?: string;
  con_desperdicio?: boolean;
  con_estructura?: boolean;
}

// Environment Installation Materials
export interface EnvironmentInstallationMaterial {
  id: number;
  environment_installation_id: number;
  descripcion: string;
  precio_unitario: number;
  cantidad: number;
  subtotal: number;
  currency: string;
  created_at: string;
  updated_at?: string;
}

export interface EnvironmentInstallationMaterialCreate {
  descripcion: string;
  precio_unitario: number;
  cantidad: number;
  subtotal: number;
  currency?: string;
}

// Installation Service (catálogo de servicios)
export interface InstallationService {
  id: number;
  trabajo: string;
  descripcion?: string;
  unidad: string;
  precio_standard?: number;
  precio_complejo?: number;
  moneda_standard: string;
  moneda_compleja: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface InstallationServiceCreate {
  trabajo: string;
  descripcion?: string;
  unidad: string;
  precio_standard?: number;
  precio_complejo?: number;
  moneda_standard?: string;
  moneda_compleja?: string;
  is_active?: boolean;
}

export type TipoInstalacion = 'standard' | 'compleja' | 'custom';

// Environment Installation
export interface EnvironmentInstallation {
  id: number;
  environment_id: number;
  installation_service_id?: number;
  tipo_instalacion?: TipoInstalacion;
  cantidad_manual: number;
  trabajo?: string;
  descripcion_trabajo?: string;
  mano_obra_m2?: number;
  unidad?: string;  // Unidad para instalaciones custom: m², ml, global, etc.
  subtotal: number;
  currency: string;
  installation_service?: InstallationService;
  materials: EnvironmentInstallationMaterial[];
  created_at: string;
  updated_at?: string;
}

export interface EnvironmentInstallationCreate {
  installation_service_id?: number;
  tipo_instalacion?: TipoInstalacion;
  cantidad_manual?: number;
  trabajo?: string;
  descripcion_trabajo?: string;
  mano_obra_m2?: number;
  unidad?: string;  // Unidad para instalaciones custom
  subtotal?: number;
  currency?: string;
}

// Environment
export interface Environment {
  id: number;
  budget_version_id: number;
  nombre: string;
  orden: number;
  descuento_materiales?: number;  // Descuento en % para materiales de este ambiente
  materials: EnvironmentMaterial[];
  installation?: EnvironmentInstallation;
  created_at: string;
  updated_at?: string;
  descripcion?: string;
}

export interface EnvironmentCreate {
  nombre: string;
  orden?: number;
  descuento_materiales?: number;
  descripcion?: string;
}

// Freight
export interface Freight {
  id: number;
  budget_version_id: number;
  tipo_envio: string;
  descripcion_detalle?: string;
  monto: number;
  currency: string;
  created_at: string;
  updated_at?: string;
  costo?: number;
  tipo?: 'fijo' | 'porcentaje';
}

export interface FreightCreate {
  tipo_envio: string;
  descripcion_detalle?: string;
  monto?: number;
  costo?: number;
  tipo?: 'fijo' | 'porcentaje';
  currency?: string;
}

// Budget Version
export interface BudgetVersion {
  id: number;
  budget_id: number;
  version_number: number;
  titulo: string;
  vendedor: string;
  email_vendedor: string;
  tel_vendedor: string;
  condiciones_contratacion: string;
  condiciones_adicionales?: string;
  descuento_materiales: number;
  descuento_instalacion?: number;
  descuento_fletes?: number;
  total_materiales: number;
  total_materiales_usd?: number;
  total_materiales_ars?: number;
  total_flete: number;
  total_instalacion: number;
  estado: 'draft' | 'sent' | 'pending_approval' | 'approved' | 'rejected' | 'closed' | 'sold' | 'lost';
  environments: Environment[];
  freights: Freight[];
  created_at: string;
  updated_at?: string;
  // Campos adicionales compatibles
  numero_version?: string;
  nombre?: string;
  condiciones?: string;
  fecha_creacion?: string;
  total_final?: number;
  is_current?: boolean;
  items?: any[];
}

export interface BudgetVersionCreate {
  titulo: string;
  vendedor: string;
  email_vendedor: string;
  tel_vendedor: string;
  condiciones_contratacion: string;
  condiciones_adicionales?: string;
  descuento_materiales?: number;
  descuento_instalacion?: number;
  descuento_fletes?: number;
  environments?: EnvironmentCreate[];
  freights?: FreightCreate[];
  // Campos adicionales para el formulario
  numero_version?: string;
  nombre?: string;
  condiciones?: string;
  // Permitir update de estado desde el frontend
  estado?: 'draft' | 'sent' | 'pending_approval' | 'approved' | 'rejected' | 'closed' | 'sold' | 'lost';
}

// Budget Summary - Para listados (sin versions)
export interface BudgetSummary {
  descuento_materiales?: number;
  descuento_instalacion?: number;
  descuento_fletes?: number;
  id: number;
  code: string;
  proyecto: string;
  cliente: string;
  status: 'draft' | 'closed' | 'sold' | 'lost';
  total_materiales?: number;
  total_flete?: number;
  total_instalacion?: number;
  total_amount?: number;
  vendedor?: string;
  current_version_number?: number;
  current_version_titulo?: string;
  current_version_fecha?: string;
  current_version_vendedor?: string;
  created_at: string;
  updated_at?: string;
}

// Budget - Esquema completo del backend
export interface Budget {
  id: number;
  code: string;
  proyecto: string;
  cliente: string;
  responsable?: string;
  email_cliente?: string;
  tel_cliente?: string;
  status: 'draft' | 'closed' | 'sold' | 'lost';
  user_id: number;
  versions: BudgetVersion[];
  created_at: string;
  updated_at?: string;
  user: {
    id: number;
    name: string;
    email: string;
  };
}

export interface BudgetCreate {
  proyecto: string;
  cliente: string;
  responsable?: string;
  email_cliente?: string;
  tel_cliente?: string;
}

// Formularios y requests
export interface BudgetFormData {
  proyecto: string;
  cliente: string;
  responsable?: string;
  email_cliente?: string;
  tel_cliente?: string;
}

export interface BudgetFilters {
  status?: string;
  search?: string;
  from_date?: string;
  to_date?: string;
  limit?: number;
  skip?: number;
}

export interface BudgetItem {
  id: number;
  product_id?: number;
  product?: Product;
  metros_cuadrados: number;
  cantidad?: number;
  con_desperdicio: boolean;
  con_estructura: boolean;
  con_terminaciones: boolean;
  con_instalacion: boolean;
  subtotal?: number;
  is_structure?: boolean;
  product_sku?: string;
  product_codigo: string;
  product_color?: string;
  product_grupo?: string;
  product_categoria?: string;
  product_largo?: number;
  product_ancho?: number;
  product_espesor?: number;
  product_estructura_m2?: string;
  product_is_active?: boolean;
  precio_unitario?: number;
}

export interface AddItemRequest {
  product_id: number;
  metros_cuadrados: number;
  con_desperdicio: boolean;
  con_estructura: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Export status types - Backend compatible
export enum BudgetStatus {
  DRAFT = 'draft',
  CLOSED = 'closed',
  SOLD = 'sold',
  LOST = 'lost'
}

// Dashboard types
export interface DashboardStats {
  total_budgets: number;
  pending_budgets: number;
  approved_budgets: number;
  total_value: number;
  recent_budgets: Budget[];
}

// Advanced Dashboard types
export interface BudgetStatusCounts {
  draft: number;
  closed: number;
  sold: number;
}

export interface AdvancedDashboardStats {
  // Estadísticas básicas
  total_budgets: number;
  budgets_this_month: number;
  total_active_products: number;
  monthly_revenue: number;
  
  // Estadísticas avanzadas
  budgets_by_status: BudgetStatusCounts;
  conversion_rate_to_approved: number;
  conversion_rate_to_sold: number;
  average_budget_value: number;
  pending_budgets: number;
  total_sold_revenue: number;
  total_approved_revenue: number;
  
  // Ventas separadas por moneda
  total_sold_revenue_usd?: number;
  total_sold_revenue_ars?: number;
  total_sold_materials_usd?: number;
  total_sold_materials_ars?: number;
  total_sold_flete_ars?: number;
  total_sold_installation_ars?: number;
}

// User Activity Stats
export interface UserActivityData {
  user_id: number;
  name: string;
  email: string;
  total_budgets: number;
  closed_budgets: number;
  approved_budgets: number;
  conversion_rate: number;
  member_since: string | null;
}

export interface UserActivityStats {
  most_active_users: UserActivityData[];
  best_conversion_users: UserActivityData[];
  total_users: number;
  active_users: number;
  inactive_users: number;
}

// User Sales Stats
export interface UserSalesData {
  user_id: number;
  name: string;
  email: string;
  total_sales_amount: number;
  total_closed_budgets: number;
  contribution_percentage?: number;
}

export interface UserSalesStats {
  top_sellers_by_amount: UserSalesData[];
  top_sellers_by_count: UserSalesData[];
  total_sales_amount: number;
}

// API Response types
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface ErrorResponse {
  detail: string;
}